<template>
  <div class="print-documents">
    <div v-if="!items || !items.length" class="empty-state">
      <p>{{ $tc('general.no_data') }}</p>
    </div>
    <div v-else>
      <div v-for="(item, index) in items" :key="itemKey(item, index)" class="document-item">
        <canvas :ref="`document-canvas-${itemKey(item, index)}`" class="document-canvas" />
      </div>
    </div>
  </div>
</template>

<script>
import { resolveTemplate } from '@/utils/documentTemplate.js'

export default {
  name: 'PrintDocuments',
  props: {
    data: { type: Array, default: () => [] },
    documentType: { type: Object, required: true },
    target: { type: String, required: true },
    entityType: { type: String, required: true, validator: v => ['tournament', 'seminar'].includes(v) },
    competitionName: { type: String, default: '' }
  },
  data () {
    return {
      loading: true,
      error: null,
      image: null
    }
  },
  computed: {
    items () {
      return this.data || []
    }
  },
  async mounted () {
    await this.loadImage()
    this.$nextTick(() => this.renderAll())
  },
  watch: {
    data: {
      handler () {
        if (this.image) {
          this.$nextTick(() => this.renderAll())
        }
      },
      deep: true
    }
  },
  methods: {
    itemKey (item, index) {
      if (item.competitor?.id) return `c-${item.competitor.id}-${index}`
      if (item.club?.id) return `club-${item.club.id}-${index}`
      return `i-${index}`
    },
    imageUrl () {
      const baseURL = this.$config?.baseURLAPI || (this.$config?.baseURL ? this.$config.baseURL + '/api' : '') || process.env.VUE_APP_API_URL || ''
      const idPath = this.entityType === 'seminar' ? 'seminar' : 'tournament'
      return `${baseURL}/${idPath}/${this.documentType.entityId}/document-types/${this.documentType.id}/image`
    },
    loadImage () {
      return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          this.image = img
          this.loading = false
          resolve()
        }
        img.onerror = () => {
          this.error = 'Failed to load document image'
          this.loading = false
          resolve()
        }
        img.src = this.imageUrl()
      })
    },
    renderAll () {
      this.items.forEach((item, index) => {
        const key = this.itemKey(item, index)
        const canvasRef = this.$refs[`document-canvas-${key}`]
        const canvasEl = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
        if (!canvasEl || !this.image) return
        this.renderOne(canvasEl, item)
      })
    },
    renderOne (canvas, item) {
      // The image is only used to size the canvas so text lands at the same
      // position/scale as in the editor - it's never drawn. The client
      // prints these pages onto paper that's already pre-printed with the
      // certificate design, so only the text may appear.
      canvas.width = this.image.width
      canvas.height = this.image.height
      const ctx = canvas.getContext('2d');
      (this.documentType.fields || []).forEach(field => {
        const text = resolveTemplate(field.template, item, this.target, this.entityType, this.competitionName)
        if (!text) return
        this.renderField(ctx, canvas, field, text)
      })
    },
    renderField (ctx, canvas, field, text) {
      const zoneWidth = (field.width || 300) * (field.scaleX || 1)
      const zoneHeight = (field.height || 80) * (field.scaleY || 1)
      const zoneX = field.left || 0
      const zoneY = field.top || 0
      const maxWidth = zoneWidth * 0.95
      const centerX = zoneX + zoneWidth / 2
      const centerY = zoneY + zoneHeight / 2

      // Rotate around the field's own center before drawing, same pattern as
      // PrintCertificates.renderTextFromTextZone: save, translate to center,
      // rotate, translate back, draw, then restore once at the end. Text
      // measurement (ctx.measureText) is unaffected by the transform, so
      // this can be applied before the font-size binary search below.
      ctx.save()
      if (field.angle) {
        ctx.translate(centerX, centerY)
        ctx.rotate((field.angle * Math.PI) / 180)
        ctx.translate(-centerX, -centerY)
      }
      // Hard backstop: the autofit loop below tries to shrink text to fit,
      // but clip the zone regardless so text can never render outside the
      // field's own box (matches the editor's clipPath - see
      // AddDocumentTypeModal's applyClipPath).
      ctx.beginPath()
      ctx.rect(zoneX, zoneY, zoneWidth, zoneHeight)
      ctx.clip()

      // fontSizePercent (percentage of the image's height) is the
      // authored ceiling for new fields - convert to actual pixels using
      // this canvas's height, which is always set to the image's natural
      // height (see renderOne), so the same percent value produces the
      // same relative text size on any resolution image. Fields saved
      // before this existed only have the old absolute-pixel `fontSize`.
      let fontSize = field.fontSizePercent != null
        ? (field.fontSizePercent / 100) * canvas.height
        : (field.fontSize || 28)
      const lines = text.split('\n')
      let allWrappedLines = []
      let minFontSize = 8
      // Treat the authored fontSize as a true ceiling: shrink to fit an
      // overflowing box, but never render larger than what the admin
      // actually authored on the canvas.
      let maxFontSize = fontSize
      let bestFontSize = minFontSize

      while (maxFontSize - minFontSize > 0.5) {
        fontSize = (minFontSize + maxFontSize) / 2
        const lineHeight = fontSize * (field.lineHeight || 1.2)
        ctx.font = `${fontSize}px ${field.fontFamily || 'Arial'}`
        allWrappedLines = []
        let widthExceeded = false
        for (const line of lines) {
          const wrapped = this.wrapTextToWidth(ctx, line, maxWidth)
          for (const w of wrapped) {
            if (ctx.measureText(w).width > maxWidth) {
              widthExceeded = true
              break
            }
          }
          if (widthExceeded) break
          allWrappedLines.push(...wrapped)
        }
        const totalHeight = allWrappedLines.length * lineHeight
        if (!widthExceeded && totalHeight <= zoneHeight * 0.95) {
          bestFontSize = fontSize
          minFontSize = fontSize
        } else {
          maxFontSize = fontSize
        }
      }

      fontSize = bestFontSize
      const lineHeight = fontSize * (field.lineHeight || 1.2)
      ctx.font = `${fontSize}px ${field.fontFamily || 'Arial'}`
      ctx.fillStyle = field.fill || '#000000'
      const textAlign = field.textAlign || 'center'
      ctx.textAlign = textAlign
      ctx.textBaseline = 'top'
      // ctx.textAlign only controls how each line is anchored *around* the
      // x passed to fillText - it doesn't pick the x itself. Always passing
      // centerX (as before) meant a 'left' field actually started at the
      // zone's horizontal center and grew rightward, matching the "text
      // renders correctly in the editor but shifted right when printed"
      // report. The anchor x has to move with the alignment: left edge for
      // 'left', right edge for 'right', center only for 'center'.
      const anchorX = textAlign === 'left' ? zoneX : textAlign === 'right' ? zoneX + zoneWidth : centerX

      allWrappedLines = []
      lines.forEach(line => allWrappedLines.push(...this.wrapTextToWidth(ctx, line, maxWidth)))
      const totalTextHeight = allWrappedLines.length * lineHeight
      const startY = zoneY + (zoneHeight - totalTextHeight) / 2

      allWrappedLines.forEach((line, i) => {
        ctx.fillText(line.trim(), anchorX, startY + i * lineHeight)
      })

      ctx.restore()
    },
    wrapTextToWidth (ctx, text, maxWidth) {
      const words = text.split(' ')
      const lines = []
      let currentLine = ''
      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word
        if (ctx.measureText(testLine).width > maxWidth && currentLine) {
          lines.push(currentLine)
          currentLine = word
        } else {
          currentLine = testLine
        }
      }
      if (currentLine) lines.push(currentLine)
      return lines.length ? lines : [text]
    }
  }
}
</script>

<style lang="scss" scoped>
.print-documents {
  .empty-state {
    padding: 40px;
    text-align: center;
    color: #909399;
  }
  .document-item {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    .document-canvas {
      display: block;
      width: 100%;
      max-width: 100%;
      height: auto;
    }
  }
}

@media print {
  * { box-sizing: border-box; }
  @page { size: A4; margin: 0; }
  body { margin: 0 !important; padding: 0 !important; }
  body > *:not(.print-documents) { display: none !important; }
  .print-documents {
    margin: 0 !important;
    padding: 0 !important;
    display: block;
    .document-item {
      margin: 0 !important;
      padding: 0 !important;
      display: block;
      page-break-inside: avoid;
      page-break-after: always;
      overflow: hidden;
      &:last-child { page-break-after: auto !important; }
      &:empty { display: none !important; page-break-after: auto !important; }
      .document-canvas {
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        display: block;
      }
    }
  }
}
</style>
